import React, { useState, useEffect } from 'react';
import { Menu, X, Zap } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 animate-slide-in-top ${
        isScrolled ? 'glass-ultra shadow-premium' : 'bg-transparent'
      }`}
    >
      {/* Smooth black fade gradient within header only */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="flex items-center justify-between h-20 relative">
          {/* Holographic Line */}
          <div className="absolute bottom-0 left-0 right-0 h-[1px] holographic-border opacity-50" />
          
          {/* Logo */}
          <div className="flex items-center group cursor-pointer">
            <div className="relative">
              <img 
                src="/rectok-logo.png" 
                alt="RECfolk" 
                className="h-8 md:h-10 w-auto transform transition-all duration-300 group-hover:scale-110 drop-shadow-[0_0_15px_rgba(255,50,37,0.5)] group-hover:drop-shadow-[0_0_25px_rgba(255,50,37,0.8)]"
              />
              <div className="absolute inset-0 bg-red-600 blur-2xl opacity-30 group-hover:opacity-50 transition-opacity animate-pulse-glow" />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {['Services', 'Showreel', 'Portfolio'].map((item, index) => (
              <button 
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="relative text-white hover:text-red-600 transition-all duration-300 text-sm font-medium tracking-wide uppercase group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {item}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-gradient-to-r from-red-600 to-white group-hover:w-full transition-all duration-300" />
                <span className="absolute inset-0 bg-red-600/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
            
            <button 
              onClick={() => scrollToSection('contact')}
              className="relative group bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-2.5 rounded-full overflow-hidden transition-all duration-300 text-sm font-medium tracking-wide uppercase neon-red hover:scale-110 hover:-translate-y-1"
            >
              <span className="relative z-10">Contact Us</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white hover:text-red-600 transition-colors duration-200 relative z-10"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden glass-ultra border-t border-white/10 animate-slide-in-top">
            <nav className="flex flex-col space-y-4 py-6">
              {['Services', 'Showreel', 'Portfolio'].map((item) => (
                <button 
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-white hover:text-red-600 transition-colors duration-200 text-sm font-medium tracking-wide uppercase text-left px-4 hover:translate-x-2 transform transition-transform"
                >
                  {item}
                </button>
              ))}
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-red-600 text-white px-6 py-2.5 rounded-full hover:bg-red-700 transition-all duration-200 text-sm font-medium tracking-wide uppercase mx-4 neon-red"
              >
                Contact Us
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
